import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { HistoryDialog } from "@/pages/MyLeave";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, History, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const fmt = (d) => format(parseISO(d), "d MMM yyyy");
const CATEGORIES = [
  { v: "illness", l: "Illness" },
  { v: "injury", l: "Injury" },
  { v: "medical", l: "Medical appointment" },
  { v: "mental_health", l: "Mental health" },
  { v: "other", l: "Other" },
];
const catLabel = (v) => CATEGORIES.find((c) => c.v === v)?.l || v;

export default function SickLeave() {
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/leave/mine", { params: { type: "sick" } }).then((r) => setRecords(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalDays = records.reduce((s, r) => s + (r.days || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight">Sick Leave</h1>
          <p className="text-sm mt-1" style={{ color: "#6A736D" }}>Log sick days — recorded instantly, no approval needed.</p>
        </div>
        <LogDialog open={open} setOpen={setOpen} onCreated={load} />
      </div>

      <div className="bg-white border rounded-lg p-5 flex items-center gap-4" style={{ borderColor: "#EAE8E1" }}>
        <span className="grid place-items-center w-11 h-11 rounded-md" style={{ backgroundColor: "#FBEDE7" }}>
          <Stethoscope className="w-5 h-5" style={{ color: "#D48B71" }} strokeWidth={1.75} />
        </span>
        <div>
          <p className="font-heading text-2xl font-medium" data-testid="total-sick-days">{totalDays} days</p>
          <p className="text-sm" style={{ color: "#6A736D" }}>logged this year across {records.length} record{records.length !== 1 ? "s" : ""}</p>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden" style={{ borderColor: "#EAE8E1" }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: "#F9F8F5" }}>
              {["Dates", "Days", "Category", "Notes", "Logged", ""].map((h) => (
                <th key={h} className="text-xs uppercase tracking-wider font-medium px-4 py-3 text-left" style={{ color: "#6A736D" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm" style={{ color: "#6A736D" }}>No sick days logged.</td></tr>
            ) : records.map((r) => (
              <tr key={r.id} className="border-b hover:bg-[#F4F3ED]/50 transition-colors" style={{ borderColor: "#EAE8E1" }} data-testid={`sick-row-${r.id}`}>
                <td className="px-4 py-3 text-sm font-medium">{fmt(r.start_date)}{r.start_date !== r.end_date ? ` – ${fmt(r.end_date)}` : ""}</td>
                <td className="px-4 py-3 text-sm">{r.days}</td>
                <td className="px-4 py-3 text-sm">{catLabel(r.category)}</td>
                <td className="px-4 py-3 text-sm" style={{ color: "#6A736D" }}>{r.note || "—"}</td>
                <td className="px-4 py-3 text-sm" style={{ color: "#6A736D" }}>{fmt(r.created_at.slice(0, 10))}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`view-sick-history-${r.id}`} onClick={() => setDetail(r)} className="text-sm flex items-center gap-1 ml-auto hover:underline" style={{ color: "#647D6C" }}>
                    <History className="w-3.5 h-3.5" /> Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <HistoryDialog detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

function LogDialog({ open, setOpen, onCreated }) {
  const [form, setForm] = useState({ start_date: "", end_date: "", category: "", note: "" });
  const [saving, setSaving] = useState(false);
  const inputCls = "mt-1.5 w-full bg-white border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#647D6C]/20 focus:border-[#647D6C]";

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/leave", {
        type: "sick",
        start_date: form.start_date,
        end_date: form.end_date || form.start_date,
        category: form.category,
        note: form.note,
      });
      toast.success("Sick leave logged");
      setOpen(false);
      setForm({ start_date: "", end_date: "", category: "", note: "" });
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to log");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="log-sick-button" className="flex items-center gap-2 text-white rounded-md px-4 py-2 font-medium transition-colors" style={{ backgroundColor: "#647D6C" }}>
          <Plus className="w-4 h-4" /> Log sick day
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Log sick leave</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium">Start date</label>
              <input data-testid="sick-start-date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} style={{ borderColor: "#EAE8E1" }} />
            </div>
            <div>
              <label className="text-sm font-medium">End date</label>
              <input data-testid="sick-end-date" type="date" value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} style={{ borderColor: "#EAE8E1" }} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Category</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="sick-category-trigger" className="mt-1.5" style={{ borderColor: "#EAE8E1" }}>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.v} value={c.v} data-testid={`category-${c.v}`}>{c.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Notes (optional)</label>
            <textarea data-testid="sick-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} style={{ borderColor: "#EAE8E1" }} placeholder="Optional details" />
          </div>
        </div>
        <DialogFooter>
          <button
            data-testid="submit-sick-button"
            onClick={submit}
            disabled={saving || !form.start_date || !form.category}
            className="text-white rounded-md px-4 py-2 font-medium disabled:opacity-50 flex items-center gap-2"
            style={{ backgroundColor: "#647D6C" }}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Log sick day
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
